/**
 * RoomProgramSection — the client-facing, LIVE view of the project's Room Program (docs/
 * ROOM_PROGRAM.md), rendered inside PreDesignEntry (MilestoneEntry.jsx) below the Report/
 * Criteria/Site-Analysis books. Unlike those, this is never a frozen PDF — room data keeps
 * evolving through Concept work, so the client always sees the current list (same "read live,
 * never stored" choice as the Drawing Sheets viewer).
 *
 * Shows Name / Room Type / approx. size / Notes only — never Cost Factor / Budget Room Cost /
 * Scope Type (those are internal budget-modeling fields). The client can suggest a rename, a
 * move to a different level, a size/notes edit, flagging a room for removal, or a new room —
 * each submitted via the existing generic respond(token, entryId, kind, payload) helper with
 * kind: 'room_suggestion'. The server (api/journey-respond.js) attributes the suggestion to
 * whichever journey_recipients token authenticated the request — this component never sends an
 * identity of its own.
 */

import { useState } from 'react'
import { respond } from '../../services/journey.js'
import { J } from '../journeyUI.jsx'

const fieldStyle = {
  width: '100%', padding: '7px 9px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5,
  border: '0.5px solid rgba(7,39,45,0.18)', borderRadius: 4, background: '#fff', color: '#17323a', boxSizing: 'border-box',
}
const smallBtn = (primary) => ({
  padding: '6px 12px', fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600,
  letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: 4, cursor: 'pointer',
  background: primary ? '#07272D' : 'none', color: primary ? '#fff' : '#07272D',
  border: primary ? 'none' : '0.5px solid rgba(7,39,45,0.3)',
})

export default function RoomProgramSection({ roomProgram, token, entryId }) {
  const [openFor, setOpenFor] = useState(null)   // room id, `new:<levelId>`, or null
  const [sentFor, setSentFor] = useState({})      // { [key]: true } — quiet confirmation, no reload

  if (!roomProgram || !roomProgram.units || !roomProgram.units.some(u => u.levels.some(l => l.rooms.length))) return null

  async function submit(action, room, level, proposed, reason) {
    const key = room ? room.id : ('new:' + level.id)
    await respond(token, entryId, 'room_suggestion', {
      action,
      roomId: room ? room.id : null,
      levelId: level ? level.id : null,
      levelName: level ? level.name : null,
      proposed,
      reason: reason || null,
    })
    setSentFor(s => ({ ...s, [key]: true }))
    setOpenFor(null)
  }

  return (
    <div style={{ marginTop: 40, paddingTop: 30, borderTop: '0.5px solid rgba(7,39,45,0.1)' }}>
      <div style={{ ...J.eyebrow, textAlign: 'center', marginBottom: 18 }}>Room Program</div>
      {roomProgram.units.map(unit => (
        <div key={unit.id} style={{ marginBottom: 26 }}>
          {roomProgram.units.length > 1 && (
            <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5c7079', marginBottom: 10 }}>{unit.name}</div>
          )}
          {unit.levels.map(level => (
            <div key={level.id} style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#17323a', marginBottom: 6 }}>{level.name}</div>
              {level.rooms.map(room => (
                <RoomLine
                  key={room.id} room={room} level={level} allLevels={unit.levels}
                  open={openFor === room.id} onToggle={() => setOpenFor(openFor === room.id ? null : room.id)}
                  sent={!!sentFor[room.id]} onSubmit={submit}
                />
              ))}
              {level.rooms.length === 0 && <div style={{ fontSize: 12, color: '#8ba6b1', fontStyle: 'italic', padding: '4px 0' }}>No rooms yet.</div>}
              <button onClick={() => setOpenFor(openFor === ('new:' + level.id) ? null : ('new:' + level.id))}
                style={{ ...smallBtn(false), marginTop: 6, fontSize: 9.5 }}>
                {sentFor['new:' + level.id] ? 'Suggestion sent ✓' : '+ Suggest a new room'}
              </button>
              {openFor === ('new:' + level.id) && (
                <NewRoomForm level={level} onCancel={() => setOpenFor(null)}
                  onSubmit={(proposed, reason) => submit('add', null, level, proposed, reason)} />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function RoomLine({ room, level, allLevels, open, onToggle, sent, onSubmit }) {
  return (
    <div style={{ padding: '9px 0', borderTop: '0.5px solid rgba(7,39,45,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 13.5, color: '#17323a' }}>{room.name || 'Room'}</span>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#5c7079' }}>{room.roomTemplateName || ''}</span>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#8ba6b1', width: 56, textAlign: 'right' }}>{room.areaManual ? room.areaManual + ' SF' : ''}</span>
        <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: '"Montserrat",sans-serif', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1a6fa8' }}>
          {sent ? 'Sent ✓' : (open ? 'Cancel' : 'Suggest a change')}
        </button>
      </div>
      {room.notes && <div style={{ fontSize: 11.5, color: '#8ba6b1', marginTop: 2, fontStyle: 'italic' }}>{room.notes}</div>}
      {open && <EditRoomForm room={room} level={level} allLevels={allLevels} onSubmit={onSubmit} />}
    </div>
  )
}

function EditRoomForm({ room, level, allLevels, onSubmit }) {
  const [name, setName] = useState(room.name || '')
  const [levelId, setLevelId] = useState(level.id)
  const [notes, setNotes] = useState(room.notes || '')
  const [reason, setReason] = useState('')
  const [remove, setRemove] = useState(false)
  const [sending, setSending] = useState(false)

  async function send() {
    setSending(true)
    try {
      if (remove) {
        await onSubmit('remove', room, level, {}, reason)
      } else {
        const targetLevel = allLevels.find(l => l.id === levelId) || level
        const proposed = {}
        if (name !== room.name) proposed.customName = name
        if (notes !== (room.notes || '')) proposed.notes = notes
        if (levelId !== level.id) { proposed.levelId = targetLevel.id; proposed.levelName = targetLevel.name }
        await onSubmit(Object.prototype.hasOwnProperty.call(proposed, 'levelId') ? 'move' : 'edit', room, targetLevel, proposed, reason)
      }
    } finally { setSending(false) }
  }

  return (
    <div style={{ marginTop: 10, padding: 14, background: 'rgba(7,39,45,0.03)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <input type="checkbox" checked={remove} onChange={e => setRemove(e.target.checked)} /> Flag this room for removal
      </label>
      {!remove && (
        <>
          <input style={fieldStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Room name" />
          {allLevels.length > 1 && (
            <select style={fieldStyle} value={levelId} onChange={e => setLevelId(e.target.value)}>
              {allLevels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <textarea style={{ ...fieldStyle, minHeight: 50 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" />
        </>
      )}
      <input style={fieldStyle} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why? (optional)" />
      <div>
        <button disabled={sending} onClick={send} style={smallBtn(true)}>{sending ? 'Sending…' : 'Send suggestion'}</button>
      </div>
    </div>
  )
}

function NewRoomForm({ level, onCancel, onSubmit }) {
  const [name, setName] = useState('')
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('')
  const [sending, setSending] = useState(false)

  async function send() {
    if (!name.trim()) return
    setSending(true)
    try { await onSubmit({ customName: name.trim(), notes }, reason) }
    finally { setSending(false) }
  }

  return (
    <div style={{ marginTop: 8, padding: 14, background: 'rgba(7,39,45,0.03)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input style={fieldStyle} autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={'Room name — on ' + level.name} />
      <textarea style={{ ...fieldStyle, minHeight: 50 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" />
      <input style={fieldStyle} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why? (optional)" />
      <div style={{ display: 'flex', gap: 8 }}>
        <button disabled={sending || !name.trim()} onClick={send} style={smallBtn(true)}>{sending ? 'Sending…' : 'Send suggestion'}</button>
        <button onClick={onCancel} style={smallBtn(false)}>Cancel</button>
      </div>
    </div>
  )
}
